import { useEffect, useMemo, useState } from "react";
import type { Hasher, Trail } from "@hash/shared";
import {
  displayHares,
  eventLabel,
  isSpecialEvent,
  isWithinCheckInWindow,
  isWithinHareAttendanceWindow,
} from "@hash/shared";
import { api, type MyHistory } from "../api/client";
import { parseApiError } from "../api/errors";
import { formatMoney } from "../lib/checkInStatus";
import { daysBetweenDates, formatTrailDateTimeShort, localDate } from "../lib/trailDate";

interface Props {
  // Trails 60 days back/forward, loaded once by main.tsx.
  trails: Trail[];
  hasherId: string;
  // A trail today the hasher hares — open the full check-in management surface.
  onManageTrail: (trail: Trail) => void;
  // An upcoming trail the hasher hares — open the details editor to prep it.
  onEditTrailDetails: (trail: Trail) => void;
  // Edit my own information (phone) — the Account screen.
  onEditProfile: () => void;
  // See my full cross-kennel history (memberships, payments, all events).
  onViewHistory: () => void;
}

/**
 * Role-based landing for everyone who isn't a misman/admin (the staff picker
 * stays in TrailPicker). The focus is on *me*: my profile + stats, today's
 * trail (with self check-in, and manage-check-in when I'm a hare), the upcoming
 * trails I'm haring (so I can prep their details), and the trails I've
 * attended. On a day with no trail this is simply a profile page.
 */
export function HomePage({
  trails,
  hasherId,
  onManageTrail,
  onEditTrailDetails,
  onEditProfile,
  onViewHistory,
}: Props) {
  const [me, setMe] = useState<Hasher | null>(null);
  const [history, setHistory] = useState<MyHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-trail self check-in confirmation: trailId -> owed message.
  const [selfDone, setSelfDone] = useState<Record<string, string>>({});

  useEffect(() => {
    api.me.get().then(setMe).catch((e) => setError(parseApiError(e).message));
    api.me
      .history()
      .then(setHistory)
      .catch((e) => setError(parseApiError(e).message));
  }, []);

  const today = localDate();
  const isHare = (t: Trail) => (t.hareHasherIds ?? []).includes(hasherId);

  // Today's events first (soonest start first); upcoming ones I'm haring next.
  const todays = trails
    .filter((t) => t.date === today)
    .sort((a, b) => (a.startAt ?? "").localeCompare(b.startAt ?? ""));
  const haringUpcoming = trails
    .filter((t) => t.date > today && isHare(t))
    .sort((a, b) => a.date.localeCompare(b.date));
  // Trails I hared on a previous day whose 48h hare-attendance window is still
  // open — so I can keep checking people in / marking paid the day after a
  // trail (mirrors canTakeAttendanceOn on the api). Today's trails live in the
  // "Today" section above; future ones in "You're haring" below.
  const haringRecent = trails
    .filter(
      (t) =>
        t.date < today && isHare(t) && isWithinHareAttendanceWindow(t, Date.now())
    )
    .sort((a, b) => b.date.localeCompare(a.date));
  const attended = history?.events ?? [];
  // Trails I've *already* checked into, from my history — so a fresh page load
  // shows my real status instead of re-prompting. selfDone only covers check-ins
  // made in this session; without this a reload would lose that I'm checked in.
  const checkedInTrailIds = useMemo(
    () => new Set((history?.events ?? []).map((e) => e.trailId)),
    [history]
  );

  const selfCheckIn = async (t: Trail) => {
    try {
      const { owed } = await api.me.checkIn(t.id);
      setSelfDone((s) => ({
        ...s,
        [t.id]:
          owed > 0
            ? `Checked in — you owe ${formatMoney(owed)} (cash to the hare).`
            : "Checked in — you're covered.",
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Self check-in failed");
    }
  };

  return (
    <div className="min-h-screen space-y-6 px-4 py-6 pb-24">
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-xl font-bold text-slate-900">
              {me?.name ?? "…"}
            </div>
            {me?.homeKennel && (
              <div className="truncate text-sm text-slate-500">
                {me.homeKennel}
              </div>
            )}
            {me?.phone && (
              <div className="text-sm text-slate-500">{me.phone}</div>
            )}
          </div>
          <button
            onClick={onEditProfile}
            className="h-9 shrink-0 rounded-lg bg-slate-100 px-3 text-sm font-medium text-slate-700 active:bg-slate-200"
          >
            Edit
          </button>
        </div>
        {history && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Stat label="Total trails" value={String(history.stats.total)} />
            <Stat
              label="Trails last 12 months"
              value={String(history.stats.last12Months)}
            />
            <Stat label="Trails hared" value={String(history.stats.hared)} />
            <Stat
              label="Days since last hare"
              value={
                history.stats.lastHaredDate
                  ? String(daysBetweenDates(history.stats.lastHaredDate, today))
                  : "—"
              }
            />
          </div>
        )}
      </section>

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {todays.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-700">Today</h2>
          <ul className="mt-2 space-y-3">
            {todays.map((t) => (
              <li
                key={t.id}
                className="rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="font-medium text-slate-900">
                  {t.kennel} {eventLabel(t)}
                  {isSpecialEvent(t) && (
                    <span className="ml-2 align-middle rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      Event
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-500">
                  {formatTrailDateTimeShort(t)}
                </div>
                <div className="truncate text-sm text-slate-500">
                  {t.startLocation}
                </div>
                {(() => {
                  const hares = displayHares(t);
                  return hares.length ? (
                    <div className="truncate text-sm text-slate-500">
                      Hares: {hares.join(", ")}
                    </div>
                  ) : (
                    <div className="truncate text-sm text-amber-700">
                      Hares: TBD
                    </div>
                  );
                })()}
                {(() => {
                  // Confirmation text: this session's owed message if I just
                  // checked in, otherwise a plain note if my history already
                  // shows me on this trail.
                  const confirmation =
                    selfDone[t.id] ??
                    (checkedInTrailIds.has(t.id) ? "Checked in." : null);
                  return confirmation ? (
                    <div className="mt-2 text-sm text-emerald-700">
                      {confirmation}
                    </div>
                  ) : null;
                })()}
                <div className="mt-3 flex gap-2">
                  {isWithinCheckInWindow(t, Date.now()) &&
                    !selfDone[t.id] &&
                    !checkedInTrailIds.has(t.id) && (
                    <button
                      onClick={() => selfCheckIn(t)}
                      className="h-11 flex-1 rounded-lg bg-slate-900 font-medium text-white active:bg-slate-700"
                    >
                      Check in
                    </button>
                  )}
                  {isHare(t) && (
                    <button
                      onClick={() => onManageTrail(t)}
                      className="h-11 flex-1 rounded-lg border border-slate-300 font-medium text-slate-700 active:bg-slate-50"
                    >
                      Manage check-in
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {haringRecent.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-700">
            Recently hared
          </h2>
          <ul className="mt-2 space-y-3">
            {haringRecent.map((t) => (
              <li
                key={t.id}
                className="rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="font-medium text-slate-900">
                  {t.kennel} {eventLabel(t)}
                  {isSpecialEvent(t) && (
                    <span className="ml-2 align-middle rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      Event
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-500">
                  {formatTrailDateTimeShort(t)}
                </div>
                <div className="truncate text-sm text-slate-500">
                  {t.startLocation}
                </div>
                <button
                  onClick={() => onManageTrail(t)}
                  className="mt-3 h-11 w-full rounded-lg border border-slate-300 font-medium text-slate-700 active:bg-slate-50"
                >
                  Manage check-in
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {haringUpcoming.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-700">You're haring</h2>
          <ul className="mt-2 space-y-3">
            {haringUpcoming.map((t) => (
              <li
                key={t.id}
                className="rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="font-medium text-slate-900">
                  {t.kennel} {eventLabel(t)}
                </div>
                <div className="text-sm text-slate-500">
                  {formatTrailDateTimeShort(t)}
                </div>
                <div className="truncate text-sm text-slate-500">
                  {t.startLocation}
                </div>
                <button
                  onClick={() => onEditTrailDetails(t)}
                  className="mt-3 h-11 w-full rounded-lg border border-slate-300 font-medium text-slate-700 active:bg-slate-50"
                >
                  Edit trail details
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">
            Trails you've attended
          </h2>
          {attended.length > 0 && (
            <button
              onClick={onViewHistory}
              className="text-sm font-medium text-slate-500 hover:text-slate-900"
            >
              See all
            </button>
          )}
        </div>
        {history == null ? (
          <p className="mt-2 text-sm text-slate-400">Loading…</p>
        ) : attended.length === 0 ? (
          <p className="mt-2 text-sm text-slate-400">
            No trails yet — your first one will be a virgin trail!
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
            {attended.slice(0, 10).map((e) => (
              <li
                key={e.checkedInAt}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm text-slate-800"
              >
                <span className="truncate">
                  {e.title ||
                    `${e.kennel ?? "?"} ${
                      e.eventType === "special"
                        ? e.name ?? "Special event"
                        : `#${e.number ?? "?"}`
                    }`}
                  {e.hare && " · hared"}
                  {e.virgin && " · virgin"}
                </span>
                <span className="text-slate-500">{e.date ?? ""}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs uppercase tracking-wide text-slate-400">
        {label}
      </div>
    </div>
  );
}
